import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {Row, Select, Calendar, Space, Col} from 'antd';
import { DropboxOutlined, TruckOutlined } from '@ant-design/icons';
import moment, {Moment} from 'moment';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useSelector } from 'react-redux';

// Store
import { useAppDispatch } from '../../../store/store';
import { getContainerShipmentSchedule } from '../../../store/redux/actions/containerShipmentActions';
import { getResources } from '../../../store/redux/actions/resourcesActions';
import * as containerShipmentSelectors from '../../../store/redux/selectors/containerShipmentSelectors';
import * as resourcesSelectors from '../../../store/redux/selectors/resourcesSelectors';

// Types
import { ContainerShipmentType } from '../../../types/containerShipmentTypes';
import PlanningFormModal from './PlanningFormModal';
import ResourcesFormModal from './ResourcesFormModal';

dayjs.locale('es');

const range = (start: number, end: number) =>
  Array.from({ length: end - start }, (_, index) => index + start);

type CalendarHeaderRenderProps = {
  value: Moment;
  onChange: (value: Moment) => void;
};

type CalendarComponentProps = {
  resourcesModalVisible: boolean;
  setResourcesModalVisible: (visible: boolean) => void;
  onVisibleRangeChange: (startDate: Moment, endDate: Moment) => void;
};

const CalendarComponent = ({ resourcesModalVisible, setResourcesModalVisible, onVisibleRangeChange }: CalendarComponentProps) => {
  const dispatch = useAppDispatch();
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const schedule = useSelector(containerShipmentSelectors.containerShipmentScheduleSelector);
  const resources = useSelector(resourcesSelectors.resourcesSelector);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [totalResources, setTotalResources] = useState<number>(0);
  const panelChangePending = useRef(false);

  const [value, setValue] = useState<Moment>(moment());

  useEffect(() => {
    const visibleMonth = moment({ year: selectedYear, month: selectedMonth });
    onVisibleRangeChange(visibleMonth.clone().startOf('month'), visibleMonth.clone().endOf('month'));
  }, [onVisibleRangeChange, selectedMonth, selectedYear]);

  useEffect(() => {
    dispatch(getContainerShipmentSchedule(selectedMonth, selectedYear));
  }, [dispatch, selectedMonth, selectedYear, resources]);

  useEffect(() => {
    dispatch(getResources());
  }, [dispatch]);

  useEffect(() => {
    const totalResources = (resources?.workers ?? 0) * (resources?.hours ?? 0) * (resources?.productivity ?? 0);

    setTotalResources(totalResources);
  }, [ resources]);

  const handleModalVisible = (visible: boolean) => {
    setFormModalVisible(visible);
  };

  const events = useMemo(() => {
    return (schedule ?? []).map((item: ContainerShipmentType) => ({
      ...item,
      scheduledUnloadingDate: moment(item.scheduledUnloadingDate),
    })).filter(event => event.scheduledUnloadingDate.isValid());
  }, [schedule]);

  const handleDateSelect = (value: Moment) => {
    if (panelChangePending.current) {
      panelChangePending.current = false;
      return;
    }

    setFormModalVisible(true);
    setValue(value);
  };

  const getHeaderRender = useCallback(({ value, onChange }: CalendarHeaderRenderProps) => {
    const handleChange = (type: 'month' | 'year', newValue: number) => {
      const now = value.clone()[type](newValue) as Moment;
      panelChangePending.current = true;
      onChange(now);
      if (type === 'month') setSelectedMonth(newValue);
      else setSelectedYear(newValue);
    };

    const renderSelect = (type: 'month' | 'year', list: number[]) => (
      <Select size='middle'
              dropdownMatchSelectWidth={false}
              value={type === 'month' ? selectedMonth : selectedYear}
              onChange={newValue => handleChange(type, newValue)}>
        {list.map(item => (
          <Select.Option key={item} value={item}>
            {type === 'month' ? dayjs().month(item).format('MMMM') : item}
          </Select.Option>
        ))}
      </Select>
    );

    return (
      <Row className='grid grid-cols-2 mb-5 gap-y-4'>
        <Col>{renderSelect('month', range(0, 12))}</Col>
        <Col className='text-right'>{renderSelect('year', range(selectedYear - 5, selectedYear + 6))}</Col>
      </Row>
    );
  }, [selectedMonth, selectedYear]);

  const dateCellRender = useCallback((value: Moment) => {
    const filtered = events.filter(event =>
      event.scheduledUnloadingDate.date() === value.date() &&
      event.scheduledUnloadingDate.month() === value.month() &&
      event.scheduledUnloadingDate.year() === value.year()
    ).sort((a, b) => a.scheduledUnloadingDate.diff(b.scheduledUnloadingDate));

    const totalCartons = filtered.reduce((sum, event) => sum + event.totalCartons, 0);
    const difference = totalResources - totalCartons;
    const warningClass = difference <= -100
      ? 'text-red-dkt-500'
      : difference < 0
        ? 'text-red-dkt-300'
        : '';

    return (
      <Col className='ant-picker-cell-inner ant-picker-calendar-date py-1 px-2 before:hidden relative max-h-52'>
        <Col className='ant-picker-calendar-date-value'>
          <Space size={4} className={warningClass}>
            <DropboxOutlined className={`text-xs flex-shrink-0 ${warningClass}`} />
            {totalCartons}
          </Space> 
        </Col>
        <Col className='absolute top-1 left-1 ant-picker-calendar-date-value'>
          Sem: {value.format('W')} - Día: {value.date()}
        </Col>
        <Col className='ant-picker-calendar-date-content'>
          <Space direction='vertical' size='small' className='min-w-full'>
            {filtered.map(event => (
              <Col key={`${event.shipmentId}-${event.scheduledUnloadingDate.format('YYYYMMDDHHmm')}`}
                   className={`w-full px-[6px] py-[2px] rounded-lg text-xs text-grey-dkt-900 border border-grey-dkt-300 ${!event.isOnTime ? ' bg-red-dkt-200 bg-dkt-red-100 border-red-300 text-red-700' : ''}`}>
                <Space size={6} className='flex items-center justify-between w-full'>
                  <Space size={4} className='flex items-center'>
                    <TruckOutlined className='text-xs flex-shrink-0' />
                    <span className='truncate'>{event.containerNumber}</span>
                  </Space>
                  <Space size={4} className='flex items-center'>
                    <DropboxOutlined className='text-xs flex-shrink-0' />
                    <span className='truncate'>{event.totalCartons}</span>
                  </Space>
                </Space>
              </Col>
            ))}
          </Space>
        </Col>
      </Col>
    );
  }, [events]);

  return (
    <>
      <Calendar mode='month'
                dateFullCellRender={dateCellRender}
                headerRender={getHeaderRender}
                className='[&>div>div>div>table>tbody>tr>td>div>div.ant-picker-calendar-date-content]:py-1 [&>div>div>div>table>tbody>tr>td>div>div.ant-picker-calendar-date-content]:h-56'
                onSelect={handleDateSelect}
                onPanelChange={date => {
                  setSelectedMonth(date.month());
                  setSelectedYear(date.year());
                }}
      />

      {formModalVisible &&
        <PlanningFormModal
          onToggleModal={handleModalVisible}
          recordsDate={value.toDate()}
        />
      }

      {resourcesModalVisible &&
        <ResourcesFormModal
          resources={resources}
          onToggleModal={setResourcesModalVisible}
        />
      }
    </>
  );
};

export default CalendarComponent;
